// src/models/apartmentModel.js - Frontend Apartment Model
import api from '../services/api';




// Apartment Management Functions

// In apartmentModel.js, check this part
export const getAllApartments = async (filters = {}) => {
    try {
        const response = await api.get('/apartments', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching apartments' };
    }
};

// Get apartment by ID
export const getApartment = async (id) => {
    try {
        const response = await api.get(`/apartments/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching apartment' };
    }
};

// Create new apartment
export const createApartment = async (apartmentData) => {
    try {
        const response = await api.post('/apartments', apartmentData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error creating apartment' };
    }
};

// Update apartment
export const updateApartment = async (id, apartmentData) => {
    try {
        const response = await api.put(`/apartments/${id}`, apartmentData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error updating apartment' };
    }
};

// Delete apartment
export const deleteApartment = async (id) => {
    try {
        const response = await api.delete(`/apartments/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error deleting apartment' };
    }
};

// Update apartment status
export const updateApartmentStatus = async (id, status) => {
    try {
        const response = await api.put(`/apartments/${id}/status`, { status });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error updating apartment status' };
    }
};

// Search apartments
export const searchApartments = async (searchTerm) => {
    try {
        const response = await api.get(`/apartments/search?q=${encodeURIComponent(searchTerm)}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error searching apartments' };
    }
};

// Get apartment statistics
export const getApartmentStats = async () => {
    try {
        const response = await api.get('/apartments/stats');
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching apartment stats' };
    }
};

// Building Management Functions

// Get all buildings
export const getAllBuildings = async () => {
    try {
        const response = await api.get('/apartments/buildings');
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching buildings' };
    }
};

// Get all apartment types
export const getAllApartmentTypes = async () => {
    try {
        const response = await api.get('/apartments/types');
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching apartment types' };
    }
};

// Helper Functions

// Format apartment display name
export const formatApartmentDisplayName = (apartment) => {
    return `${apartment.buildingName} - ${apartment.apartmentNumber}`;
};

// Get status color
export const getApartmentStatusColor = (status, isDarkMode = false) => {
    const s = (status || '').toLowerCase();
    const colors = {
        vacant: isDarkMode ? 'bg-blue-600/10 text-blue-400' : 'bg-blue-50 text-blue-600',
        occupied: isDarkMode ? 'bg-zinc-800/40 text-zinc-400' : 'bg-zinc-100 text-zinc-600',
        maintenance: isDarkMode ? 'bg-amber-600/10 text-amber-500' : 'bg-amber-50 text-amber-600',
        paid: isDarkMode ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600',
        partial: isDarkMode ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600',
        overdue: isDarkMode ? 'bg-rose-500/10 text-rose-400' : 'bg-rose-600/10 text-rose-600',
        pending: isDarkMode ? 'bg-zinc-800/40 text-zinc-400' : 'bg-zinc-100 text-zinc-600'
    };

    return colors[s] || (isDarkMode ? 'bg-zinc-950/20 text-zinc-400' : 'bg-gray-100 text-gray-800');
};

// Format apartment details
export const formatApartmentDetails = (apartment) => {
    if (!apartment) return '';

    let details = '';
    if (apartment.floor) {
        details += `Floor ${apartment.floor}`;
    }

    if (apartment.type) {
        if (details) details += ' • ';
        details += apartment.type;
    }

    return details;
};

// Format currency
export const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

// Validate apartment data
export const validateApartmentData = (apartmentData) => {
    const errors = {};

    if (!apartmentData.apartmentNumber?.trim()) {
        errors.apartmentNumber = 'Apartment number is required';
    }

    if (!apartmentData.floor || apartmentData.floor < 0) {
        errors.floor = 'Valid floor number is required';
    }

    if (!apartmentData.buildingId) {
        errors.buildingId = 'Building is required';
    }

    if (!apartmentData.type?.trim()) {
        errors.type = 'Apartment type is required';
    }

    if (!apartmentData.size || apartmentData.size <= 0) {
        errors.size = 'Size must be greater than 0';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// Apartment status options
export const APARTMENT_STATUS_OPTIONS = [
    { value: 'vacant', label: 'Vacant' },
    { value: 'occupied', label: 'Occupied' },
    { value: 'maintenance', label: 'Under Maintenance' }
];

export default {
    getAllApartments,
    getApartment,
    createApartment,
    updateApartment,
    deleteApartment,
    updateApartmentStatus,
    searchApartments,
    getApartmentStats,
    getAllBuildings,
    getAllApartmentTypes,
    formatApartmentDisplayName,
    getApartmentStatusColor,
    formatApartmentDetails,
    formatCurrency,
    validateApartmentData,
    APARTMENT_STATUS_OPTIONS
};